import AppIconButton from '@nxsys/components/AppIconButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetActivityLogEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { ActivityLog as IActivityLog } from '@nxsys/types/models/activityLogs';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import LogDetailsModal from '../../activityLogs/Modals/LogDetailsModal';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
};

const ActivityLog: React.FC<Props> = ({ employeeId }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const { activityLogList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = activityLogList;

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetActivityLogEmployeeListParams) => {
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getActivityLogEmployeeList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IActivityLog | null>(null);

  const toggleViewModal = (record?: IActivityLog) => {
    setOpenViewModal(!openViewModal);
    setSelectedData(record || null);
  };

  const columns: ColumnsType<IActivityLog> = [
    {
      key: 'actions',
      title: messages['common.action'],
      width: 80,
      align: 'left',
      render: (record: IActivityLog) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li>
            <AppIconButton
              title={messages['common.view']}
              icon={<Icons.IconEye />}
              onClick={() => toggleViewModal(record)}
            />
          </li>
        </ul>
      ),
    },
    {
      key: 'subcontractorRef',
      title: isBureau
        ? (messages['common.bureau.company'] as string)
        : (messages['activityLogs.agencyRef'] as string),
      dataIndex: 'subcontractorRef',
      width: 160,
      render: (value: IActivityLog['subcontractorRef']) =>
        `${value?.primaryAgency?.agencyPrefix || '_'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
    },
    {
      key: 'subcontractorRef',
      title: isBureau
        ? (messages['activityLogs.bureau.employee'] as string)
        : (messages['activityLogs.employee'] as string),
      dataIndex: 'subcontractorRef',
      width: 180,
      render: (value: IActivityLog['subcontractorRef']) =>
        `${value?.forename || ''} ${value?.surname || ''}`,
    },
    {
      key: 'logDetails',
      title: messages['common.logDetails'],
      dataIndex: 'logDetails',
      width: 450,
    },
    {
      key: 'updatedAt',
      title: messages['common.lastUpdated'],
      dataIndex: 'updatedAt',
      width: 200,
      render: (value: string) =>
        value
          ? moment(value).tz('Europe/London').format('DD/MM/YYYY @ hh.mm a')
          : '_',
    },
    {
      key: 'updatedBy',
      title: messages['common.updatedBy'],
      width: 180,
      render: (record: IActivityLog) => {
        const { updatedBy, isSuperAdmin } = record;
        return isSuperAdmin
          ? 'Super Admin'
          : `${updatedBy?.forename || ''} ${updatedBy?.surname || ''}`;
      },
    },
  ];

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.activityLog']}
      </Typography.Title>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          titleEmpty={messages['activityLogs.empty']}
          columns={columns}
          scroll={{ x: 1250, y: 600 }}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
      {selectedData && (
        <LogDetailsModal
          visible={openViewModal}
          data={selectedData as any}
          toggle={toggleViewModal}
          type="employee"
        />
      )}
    </div>
  );
};

export default ActivityLog;
